import { X, MapPin } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import type { Zone, Sensor, Operator } from '@/types'
import { useMemo } from 'react'

interface ZoneSidebarProps {
  zones: Zone[]
  sensors: Sensor[]
  operators?: Operator[]
  isOpen: boolean
  onClose: () => void
  onZoneClick: (zone: Zone) => void
  userLocation?: { latitude: number; longitude: number } | null
}

// Calculate distance between two points using Haversine formula (in km)
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371 // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export function ZoneSidebar({ zones, sensors, operators = [], isOpen, onClose, onZoneClick, userLocation }: ZoneSidebarProps) {
  // Create operator lookup map
  const operatorMap = useMemo(() => {
    return new Map(operators.map(op => [op.id, op]))
  }, [operators])
  
  // Calculate stats for each zone
  const zonesWithStats = useMemo(() => {
    return zones.map(zone => {
      const zoneSensors = sensors.filter(s => s.zone_id === zone.zone_id)
      const available = zoneSensors.filter(s => s.occupancy_status === false).length
      const occupied = zoneSensors.filter(s => s.occupancy_status === true).length
      
      return {
        zone,
        available,
        occupied,
        total: zoneSensors.length
      }
    })
  }, [zones, sensors])

  // Sort zones by distance from user
  const sortedZones = useMemo(() => {
    if (!userLocation) {
      // If no user location, just return zones as-is
      return zonesWithStats.map(item => ({
        ...item,
        distance: null
      }))
    }

    return zonesWithStats
      .map(item => {
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          item.zone.center_coordinates.lat,
          item.zone.center_coordinates.lon
        )
        return { ...item, distance }
      })
      .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))
  }, [zonesWithStats, userLocation])

  const totalAvailable = sortedZones.reduce((sum, item) => sum + item.available, 0)
  const totalOccupied = sortedZones.reduce((sum, item) => sum + item.occupied, 0)
  const totalSpots = totalAvailable + totalOccupied

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 z-[1000] md:hidden"
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={{ x: '100%' }}
        animate={{ x: isOpen ? 0 : '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed right-0 top-0 bottom-0 w-full sm:w-80 bg-background border-l border-border shadow-lg z-[1100] flex flex-col"
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">Parking Zones</h2>
            <p className="text-sm text-muted-foreground">
              {zones.length} zone{zones.length !== 1 ? 's' : ''} · {totalSpots} spot{totalSpots !== 1 ? 's' : ''}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-10 w-10 p-0 min-w-[44px] min-h-[44px]"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-center gap-6 sm:gap-4 p-4 bg-muted/50 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 sm:w-3 sm:h-3 rounded-full bg-green-500" />
            <div>
              <p className="text-xs text-muted-foreground">Available</p>
              <p className="text-xl sm:text-lg font-semibold">{totalAvailable}</p>
            </div>
          </div>
          <div className="w-px h-12 sm:h-10 bg-border" />
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 sm:w-3 sm:h-3 rounded-full bg-red-500" />
            <div>
              <p className="text-xs text-muted-foreground">Occupied</p>
              <p className="text-xl sm:text-lg font-semibold">{totalOccupied}</p>
            </div>
          </div>
        </div>

        {/* Zone List */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {sortedZones.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <MapPin size={48} className="text-muted-foreground mb-4 opacity-50" />
              <p className="text-muted-foreground">No zones found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {sortedZones.map(({ zone, available, occupied, total }) => {
                const occupancyRate = total > 0 ? (occupied / total) * 100 : 0

                return (
                  <button
                    key={zone.zone_id}
                    onClick={() => {
                      onZoneClick(zone)
                      // Always close sidebar on mobile for better UX
                      if (window.innerWidth < 768) {
                        onClose()
                      }
                    }}
                    className="w-full p-4 text-left hover:bg-muted/50 active:bg-muted transition-colors min-h-[72px] tap-highlight"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate">{zone.display_name}</h3>
                        {operatorMap.get(zone.operator_id) && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {operatorMap.get(zone.operator_id)?.display_name}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin size={12} />
                            <span>
                              {available}/{total} available
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Occupancy Badge */}
                      <div className="flex-shrink-0">
                        {available > 0 ? (
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            occupancyRate < 50
                              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                              : occupancyRate < 80
                              ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                              : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                          }`}>
                            {Math.round(occupancyRate)}% full
                          </div>
                        ) : (
                          <div className="px-2 py-1 rounded text-xs font-medium bg-red-500/10 text-red-600 dark:text-red-400">
                            Full
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {total > 0 && (
                      <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            occupancyRate < 50
                              ? 'bg-green-500'
                              : occupancyRate < 80
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${occupancyRate}%` }}
                        />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </motion.aside>
    </>
  )
}
